import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── brookebit chrome palette (shared vocabulary with the other simulators) ──────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
};

// ── CRT terminal palette (the game panel only) ─────────────────────────────────
const P = {
  screen:   '#0a0f0a',
  bar:      '#101710',
  bright:   '#b6ffce',
  normal:   '#7be8a0',
  dim:      '#5a8f66',
  faint:    '#3f6b4a',
  amber:    '#e0b23a',
  red:      '#e2564a',
  edge:     '#1f3d24',
  runeOff:  '#2c5a38',
  runeOn:   '#4fd07a',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";
const glow = '0 0 4px rgba(80,220,130,0.35)';

// ── The seven chambers ─────────────────────────────────────────────────────────
// `truth` lists the output Z for each input row, in order. For a 2-input gate the
// rows are 00, 01, 10, 11; for NOT the rows are 0, 1. A row is "winning" when Z = 1
// — those are the patterns the player must step on to light the gate.
const CHAMBERS = [
  {
    id: 'AND', name: 'AND', inputs: 2, truth: [0, 0, 0, 1],
    title: 'THE HALL OF AND',
    riddle: 'Output is 1 only when A AND B are both 1.',
    story: 'The first door. Two rune-stones, A and B, are set in the floor. Words are etched above them: "I yield only when BOTH of us burn."',
  },
  {
    id: 'OR', name: 'OR', inputs: 2, truth: [0, 1, 1, 1],
    title: 'THE FLOODED HALL',
    riddle: 'Output is 1 when A OR B (or both) are 1.',
    story: 'Black water laps at a sunken causeway. A hum rises from it: "Any single spark will carry you across. Only true darkness drowns you."',
  },
  {
    id: 'NOT', name: 'NOT', inputs: 1, truth: [1, 0],
    title: 'THE MIRROR STONE',
    riddle: 'One input. Output is the opposite of A.',
    story: 'A single mirror-stone, A, waits in an empty vault. "I am the inverter," it whispers. "I give back only the opposite of what I am given."',
  },
  {
    id: 'NAND', name: 'NAND', inputs: 2, truth: [1, 1, 1, 0],
    title: 'THE BROKEN ENGINE',
    riddle: "Output is 0 only when both inputs are 1 (it is NOT AND).",
    story: 'Rusted gears grind overhead. "I am AND’s shadow," the engine groans. "I hold firm for you always — and fail you only when the work is whole."',
  },
  {
    id: 'NOR', name: 'NOR', inputs: 2, truth: [1, 0, 0, 0],
    title: 'THE SILENT CRYPT',
    riddle: 'Output is 1 only when both inputs are 0 (it is NOT OR).',
    story: 'A crypt of perfect stillness. A voice barely stirs the air: "I wake in total darkness alone. Let a single spark rise, and I sleep."',
  },
  {
    id: 'XOR', name: 'XOR', inputs: 2, truth: [0, 1, 1, 0],
    title: 'THE XOR CHASM',
    riddle: 'Output is 1 only when the inputs DIFFER.',
    story: 'A rift splits the floor. Two crystals, A and B, pulse in the dark: "The bridge forms only where we disagree. Match us, and you fall."',
  },
  {
    id: 'XNOR', name: 'XNOR', inputs: 2, truth: [1, 0, 0, 1],
    title: 'THE LAST SEAL',
    riddle: 'Output is 1 only when the inputs MATCH.',
    story: 'Daylight leaks around one final seal. Its runes glow faintly: "Only sameness opens me. Where the stones agree, I part — nowhere else."',
  },
];

// Row label for input row `r` of a gate with `inputs` inputs (2 → "A=0  B=1").
function rowLabel(r, inputs) {
  if (inputs === 1) return `A=${r}`;
  const a = (r >> 1) & 1, b = r & 1;
  return `A=${a} B=${b}`;
}

// Indices of the winning rows (Z = 1) for a chamber.
function winningRows(ch) {
  return ch.truth.reduce((acc, z, i) => (z === 1 ? [...acc, i] : acc), []);
}

// ── Logic-gate symbols (distinctive / MIL-shape), drawn in phosphor green ───────
function GateSymbol({ id, size = 60 }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' };
  const parts = {
    AND: (<>
      <path d="M16 8 H32 A12 12 0 0 1 32 32 H16 Z" {...common} />
      <line x1="6" y1="15" x2="16" y2="15" {...common} />
      <line x1="6" y1="25" x2="16" y2="25" {...common} />
      <line x1="44" y1="20" x2="56" y2="20" {...common} />
    </>),
    NAND: (<>
      <path d="M16 8 H32 A12 12 0 0 1 32 32 H16 Z" {...common} />
      <circle cx="47" cy="20" r="3" {...common} />
      <line x1="6" y1="15" x2="16" y2="15" {...common} />
      <line x1="6" y1="25" x2="16" y2="25" {...common} />
      <line x1="50" y1="20" x2="56" y2="20" {...common} />
    </>),
    OR: (<>
      <path d="M14 8 Q22 20 14 32 Q34 32 46 20 Q34 8 14 8 Z" {...common} />
      <line x1="6" y1="15" x2="17" y2="15" {...common} />
      <line x1="6" y1="25" x2="17" y2="25" {...common} />
      <line x1="46" y1="20" x2="56" y2="20" {...common} />
    </>),
    NOR: (<>
      <path d="M14 8 Q22 20 14 32 Q34 32 46 20 Q34 8 14 8 Z" {...common} />
      <circle cx="49" cy="20" r="3" {...common} />
      <line x1="6" y1="15" x2="17" y2="15" {...common} />
      <line x1="6" y1="25" x2="17" y2="25" {...common} />
      <line x1="52" y1="20" x2="56" y2="20" {...common} />
    </>),
    XOR: (<>
      <path d="M17 8 Q25 20 17 32 Q37 32 49 20 Q37 8 17 8 Z" {...common} />
      <path d="M11 8 Q19 20 11 32" {...common} />
      <line x1="4" y1="15" x2="14" y2="15" {...common} />
      <line x1="4" y1="25" x2="14" y2="25" {...common} />
      <line x1="49" y1="20" x2="56" y2="20" {...common} />
    </>),
    XNOR: (<>
      <path d="M17 8 Q25 20 17 32 Q37 32 49 20 Q37 8 17 8 Z" {...common} />
      <path d="M11 8 Q19 20 11 32" {...common} />
      <circle cx="52" cy="20" r="3" {...common} />
      <line x1="4" y1="15" x2="14" y2="15" {...common} />
      <line x1="4" y1="25" x2="14" y2="25" {...common} />
      <line x1="55" y1="20" x2="59" y2="20" {...common} />
    </>),
    NOT: (<>
      <path d="M18 8 L18 32 L42 20 Z" {...common} />
      <circle cx="45" cy="20" r="3" {...common} />
      <line x1="6" y1="20" x2="18" y2="20" {...common} />
      <line x1="48" y1="20" x2="56" y2="20" {...common} />
    </>),
  };
  return (
    <svg viewBox="0 0 60 40" width={size} height={size * 40 / 60} aria-hidden="true"
      style={{ color: P.runeOn, filter: 'drop-shadow(0 0 3px rgba(80,220,130,0.4))', display: 'block' }}>
      {parts[id]}
    </svg>
  );
}

// ── Typewriter narration (respects prefers-reduced-motion; click panel to skip) ─
function Typewriter({ text, animate, onDone }) {
  const [shown, setShown] = useState(animate ? '' : text);
  const [done, setDone]   = useState(!animate);
  const iRef = useRef(0);
  const tRef = useRef(null);

  useEffect(() => {
    clearInterval(tRef.current);
    if (!animate) { setShown(text); setDone(true); onDone && onDone(); return; }
    setShown(''); setDone(false); iRef.current = 0;
    tRef.current = setInterval(() => {
      iRef.current += 1;
      setShown(text.slice(0, iRef.current));
      if (iRef.current >= text.length) {
        clearInterval(tRef.current);
        setDone(true); onDone && onDone();
      }
    }, 16);
    return () => clearInterval(tRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animate]);

  // Expose a skip via a data attribute the panel reads on click
  Typewriter._skip = () => {
    clearInterval(tRef.current);
    setShown(text); setDone(true); onDone && onDone();
  };

  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {shown}
      {!done && <span style={{ opacity: 0.8 }}>▋</span>}
    </span>
  );
}

// ── CRT window frame (module-level so it never remounts on parent re-render) ────
function CrtScreen({ label, children, s, crt, reducedMotion, onSkip }) {
  return (
    <div style={s.screen} onClick={onSkip}>
      <div style={s.winBar}>
        <span style={s.dot(P.red)} /><span style={s.dot(P.amber)} /><span style={s.dot(P.runeOn)} />
        <span style={s.winTitle}>{label}</span>
      </div>
      <div style={s.inner}>{children}</div>
      {crt && !reducedMotion && <div style={s.scanlines} />}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────────
export default function LogicGateAdventure({ onBack }) {
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [phase, setPhase] = useState('intro');   // intro | chamber | cleared | death | victory
  const [idx, setIdx]     = useState(0);          // current chamber index
  const [lit, setLit]     = useState([]);         // winning rows stepped on this chamber
  const [deaths, setDeaths] = useState(0);
  const [mode, setMode]   = useState('explorer'); // explorer (respawn) | iron (full restart)
  const [crt, setCrt]     = useState(!reducedMotion);
  const [narrDone, setNarrDone] = useState(false);

  const ch  = CHAMBERS[idx];
  const win = winningRows(ch);
  const animate = crt && !reducedMotion;

  // Reset per-chamber narration state whenever we enter a chamber
  useEffect(() => {
    if (phase === 'chamber') { setNarrDone(!animate); }
  }, [phase, idx, animate]);

  const enterChamber = useCallback((i) => {
    setIdx(i); setLit([]); setPhase('chamber');
  }, []);

  const beginRun = useCallback(() => {
    setDeaths(0); enterChamber(0);
  }, [enterChamber]);

  const stepOn = useCallback((r) => {
    if (phase !== 'chamber') return;
    if (lit.includes(r)) return;
    if (win.includes(r)) {
      const next = [...lit, r];
      setLit(next);
      if (next.length === win.length) setPhase('cleared');
    } else {
      setDeaths(d => d + 1);
      setPhase('death');
    }
  }, [phase, lit, win]);

  const retry = useCallback(() => {
    if (mode === 'iron') { setLit([]); setIdx(0); setPhase('chamber'); }
    else { setLit([]); setPhase('chamber'); }
  }, [mode]);

  const advance = useCallback(() => {
    if (idx + 1 >= CHAMBERS.length) setPhase('victory');
    else enterChamber(idx + 1);
  }, [idx, enterChamber]);

  const skipTyping = useCallback(() => {
    if (Typewriter._skip) Typewriter._skip();
  }, []);

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '0 1.5rem', height: 60, gap: '1rem',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
    },
    body: { maxWidth: 760, margin: '0 auto', padding: '1.75rem 1.5rem 3rem' },

    // CRT screen
    screen: {
      position: 'relative', background: P.screen, border: `1px solid ${P.edge}`,
      borderRadius: 12, overflow: 'hidden', fontFamily: mono, color: P.normal,
      boxShadow: 'inset 0 0 70px rgba(60,220,120,0.05)',
    },
    winBar: {
      display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
      background: P.bar, borderBottom: `1px solid ${P.edge}`,
    },
    dot: (c) => ({ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }),
    winTitle: { marginLeft: 8, color: P.dim, fontSize: '0.72rem', letterSpacing: '0.06em' },
    inner: { padding: '20px 22px', fontSize: '0.86rem', lineHeight: 1.75 },

    stepGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 10, margin: '14px 0 6px',
    },
    rune: (state) => ({
      textAlign: 'left', cursor: state === 'lit' ? 'default' : 'pointer',
      fontFamily: mono, fontSize: '0.9rem',
      border: `1px solid ${state === 'lit' ? P.runeOn : P.runeOff}`,
      background: state === 'lit' ? 'rgba(79,208,122,0.16)' : 'rgba(60,220,120,0.04)',
      color: state === 'lit' ? P.bright : P.normal,
      borderRadius: 8, padding: '11px 13px', display: 'flex',
      justifyContent: 'space-between', alignItems: 'center',
      textShadow: glow, transition: 'background 0.15s, border-color 0.15s',
    }),
    prompt: { color: P.dim, margin: '4px 0 2px' },
    statusRow: {
      marginTop: 16, paddingTop: 12, borderTop: `1px dashed ${P.edge}`,
      color: P.dim, fontSize: '0.76rem', display: 'flex',
      justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
    },
    crtBtn: (primary) => ({
      fontFamily: mono, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
      color: primary ? P.screen : P.normal,
      background: primary ? P.runeOn : 'transparent',
      border: `1px solid ${P.runeOn}`, borderRadius: 8, padding: '9px 18px',
      textShadow: primary ? 'none' : glow, letterSpacing: '0.03em',
    }),
    scanlines: {
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5, borderRadius: 12,
      background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 1px, transparent 1px, transparent 3px)',
      opacity: 0.55,
    },
    toggle: (on) => ({
      fontFamily: mono, fontSize: '0.72rem', cursor: 'pointer', padding: '4px 10px',
      borderRadius: 6, border: `1px solid ${on ? P.runeOn : P.runeOff}`,
      background: on ? 'rgba(79,208,122,0.15)' : 'transparent',
      color: on ? P.bright : P.dim, textShadow: on ? glow : 'none',
    }),

    // brookebit explainer card under the panel
    card: {
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: '1.25rem', marginTop: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
  };

  // ── Panel content per phase ────────────────────────────────────────────────
  let panel;

  if (phase === 'intro') {
    panel = (
      <CrtScreen s={s} crt={crt} reducedMotion={reducedMotion} onSkip={skipTyping} label="the boolean caverns — v1.0">
        <pre style={{ color: P.runeOn, textShadow: glow, fontSize: '0.72rem', lineHeight: 1.25, margin: '0 0 14px', overflowX: 'auto' }}>{
`  ___ ___  ___  _    ___   _   _  _
 | _ ) _ \\/ _ \\| |  | __| /_\\ | \\| |
 | _ \\  _/ (_) | |__| _| / _ \\| .' |
 |___/_|  \\___/|____|___/_/ \\_\\_|\\_|
      T H E   C A V E R N S`
        }</pre>
        <div style={{ color: P.bright, textShadow: glow, marginBottom: 8 }}>&gt; You wake in the dark, far below the world.</div>
        <Typewriter animate={animate} onDone={() => setNarrDone(true)} text={
          'Seven chambers stand between you and daylight. Each is sealed by a logic gate, and each gate opens only for the input patterns that make its output 1. Step onto a dead pattern and the dark resolves you to NULL.\n\nKnow your gates. Step wisely.'
        } />
        <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: P.dim, fontSize: '0.74rem', marginRight: 4 }}>mode:</span>
          <span style={s.toggle(mode === 'explorer')} onClick={(e) => { e.stopPropagation(); setMode('explorer'); }}>Explorer · respawn in room</span>
          <span style={s.toggle(mode === 'iron')} onClick={(e) => { e.stopPropagation(); setMode('iron'); }}>Iron adventurer · full restart</span>
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: P.dim, fontSize: '0.74rem', marginRight: 4 }}>screen:</span>
          <span style={s.toggle(crt)} onClick={(e) => { e.stopPropagation(); setCrt(c => !c); }}>{crt ? 'CRT scanlines ON' : 'CRT scanlines OFF'}</span>
        </div>
        <div style={{ marginTop: 20 }}>
          <button style={s.crtBtn(true)} onClick={(e) => { e.stopPropagation(); beginRun(); }}>▸ Begin the descent</button>
        </div>
      </CrtScreen>
    );
  } else if (phase === 'chamber') {
    panel = (
      <CrtScreen s={s} crt={crt} reducedMotion={reducedMotion} onSkip={skipTyping} label={`the boolean caverns — chamber ${idx + 1} of ${CHAMBERS.length}`}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flexShrink: 0 }}><GateSymbol id={ch.id} size={72} /></div>
          <div style={{ flex: '1 1 220px', minWidth: 200 }}>
            <div style={{ color: P.bright, textShadow: glow, letterSpacing: '0.04em' }}>&gt; {ch.title}</div>
            <div style={{ color: P.amber, fontSize: '0.78rem', margin: '2px 0 8px' }}>
              {ch.name} gate{ch.inputs === 1 ? ' · 1 input' : ' · 2 inputs'}
            </div>
            <div style={{ color: P.normal, minHeight: '3.4em' }}>
              <Typewriter animate={animate} onDone={() => setNarrDone(true)} text={ch.story} />
            </div>
          </div>
        </div>

        {narrDone && (<>
          <div style={s.prompt}>&gt; Light every pattern that energises the gate (output = 1). One wrong stone and the dark takes you.</div>
          <div style={s.stepGrid}>
            {ch.truth.map((_, r) => {
              const isLit = lit.includes(r);
              return (
                <button key={r} style={s.rune(isLit ? 'lit' : 'off')} disabled={isLit}
                  onClick={(e) => { e.stopPropagation(); stepOn(r); }}>
                  <span>{rowLabel(r, ch.inputs)}</span>
                  <span style={{ fontSize: '0.72rem', color: isLit ? P.runeOn : P.faint }}>
                    {isLit ? '✓ lit' : 'step'}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={s.statusRow}>
            <span>torches lit: <span style={{ color: P.bright }}>{lit.length} / {win.length}</span></span>
            <span style={{ color: P.amber }}>deaths: {deaths}</span>
          </div>
        </>)}
      </CrtScreen>
    );
  } else if (phase === 'cleared') {
    panel = (
      <CrtScreen s={s} crt={crt} reducedMotion={reducedMotion} onSkip={skipTyping} label={`the boolean caverns — chamber ${idx + 1} cleared`}>
        <div style={{ color: P.bright, textShadow: glow, marginBottom: 6 }}>&gt; The gate flares to life. You identified the {ch.name} gate.</div>
        <div style={{ color: P.dim, borderLeft: `2px solid ${P.runeOn}`, paddingLeft: 10, margin: '10px 0 12px' }}>
          <span style={{ color: P.amber }}>the rule:</span> {ch.riddle}
        </div>
        <div style={{ color: P.dim, marginBottom: 12 }}>Its full truth table, burned into the wall:</div>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.9rem', margin: '0 0 6px' }}>
          <thead>
            <tr>
              <th style={{ padding: '3px 16px 3px 0', textAlign: 'left', color: P.dim }}>A</th>
              {ch.inputs === 2 && <th style={{ padding: '3px 16px 3px 0', textAlign: 'left', color: P.dim }}>B</th>}
              <th style={{ padding: '3px 0', textAlign: 'left', color: P.dim }}>Z</th>
            </tr>
          </thead>
          <tbody>
            {ch.truth.map((z, r) => {
              const a = ch.inputs === 2 ? (r >> 1) & 1 : r;
              const b = r & 1;
              const on = z === 1;
              return (
                <tr key={r} style={{ color: on ? P.bright : P.dim, textShadow: on ? glow : 'none' }}>
                  <td style={{ padding: '3px 16px 3px 0' }}>{a}</td>
                  {ch.inputs === 2 && <td style={{ padding: '3px 16px 3px 0' }}>{b}</td>}
                  <td style={{ padding: '3px 0', color: on ? P.runeOn : P.faint }}>{z}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 18 }}>
          <button style={s.crtBtn(true)} onClick={(e) => { e.stopPropagation(); advance(); }}>
            {idx + 1 >= CHAMBERS.length ? '▸ Step into the light' : '▸ Open the next door'}
          </button>
        </div>
      </CrtScreen>
    );
  } else if (phase === 'death') {
    panel = (
      <CrtScreen s={s} crt={crt} reducedMotion={reducedMotion} onSkip={skipTyping} label="the boolean caverns — resolved to NULL">
        <pre style={{ color: P.red, fontSize: '0.72rem', lineHeight: 1.25, margin: '0 0 12px', overflowX: 'auto', textShadow: '0 0 5px rgba(226,86,74,0.4)' }}>{
`  _   _ _   _ _    _
 | \\ | | | | | |  | |
 |  \\| | | | | |  | |
 | . \` | | | | |  | |
 |_|\\_|\\___/|_|__|_|_
    R E S O L V E D`
        }</pre>
        <div style={{ color: P.red, textShadow: '0 0 5px rgba(226,86,74,0.35)', marginBottom: 8 }}>
          &gt; The floor gives way. The dark folds over you and you are resolved to NULL.
        </div>
        <div style={{ color: P.dim }}>
          That pattern gave output <span style={{ color: P.red }}>0</span> — the gate stayed shut.
          {mode === 'iron'
            ? ' Iron adventurer: the caverns reset to the very first chamber.'
            : ` You reawaken at the mouth of ${ch.title.toLowerCase()}.`}
        </div>
        <div style={{ color: P.amber, marginTop: 10, fontSize: '0.8rem' }}>deaths: {deaths}</div>
        <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button style={s.crtBtn(true)} onClick={(e) => { e.stopPropagation(); retry(); }}>▸ Rise and try again</button>
          <button style={s.crtBtn(false)} onClick={(e) => { e.stopPropagation(); setPhase('intro'); }}>Abandon run</button>
        </div>
      </CrtScreen>
    );
  } else { // victory
    panel = (
      <CrtScreen s={s} crt={crt} reducedMotion={reducedMotion} onSkip={skipTyping} label="the boolean caverns — escaped">
        <pre style={{ color: P.runeOn, textShadow: glow, fontSize: '0.72rem', lineHeight: 1.25, margin: '0 0 14px', overflowX: 'auto' }}>{
` __   __         _    _
 \\ \\ / /__ _  _  | |__| |
  \\ V / _ \\ || | | / _\` |
   |_|\\___/\\_,_| |_\\__,_|
   E S C A P E D !`
        }</pre>
        <Typewriter animate={animate} text={
          'Daylight pours over you as the last seal parts. Seven gates read, seven doors opened — you climb out of the Boolean Caverns and into the sun.'
        } />
        <div style={{ marginTop: 16, color: P.dim }}>
          chambers cleared: <span style={{ color: P.bright }}>{CHAMBERS.length}</span>
          <span style={{ margin: '0 8px', color: P.faint }}>·</span>
          deaths along the way: <span style={{ color: P.amber }}>{deaths}</span>
          <span style={{ margin: '0 8px', color: P.faint }}>·</span>
          mode: <span style={{ color: P.bright }}>{mode === 'iron' ? 'Iron adventurer' : 'Explorer'}</span>
        </div>
        <div style={{ marginTop: 8, color: deaths === 0 ? P.runeOn : P.dim, fontSize: '0.82rem' }}>
          {deaths === 0 ? '★ Flawless descent — not a single wrong stone.' : 'Every logician stumbles. Try for a flawless run?'}
        </div>
        <div style={{ marginTop: 18 }}>
          <button style={s.crtBtn(true)} onClick={(e) => { e.stopPropagation(); setPhase('intro'); }}>▸ Descend again</button>
        </div>
      </CrtScreen>
    );
  }

  return (
    <div style={s.wrap}>
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>The Boolean Caverns</h1>
        <span style={s.badge}>A1.2.3 · A1.2.5</span>
      </header>

      <main style={s.body}>
        {panel}

        <div style={s.card}>
          <div style={s.sectionLabel}>What you are practising</div>
          <p style={{ fontSize: '0.88rem', color: B.text, lineHeight: 1.6, margin: '0 0 0.6rem' }}>
            Each chamber is one of the seven logic gates from the syllabus: <strong>AND, OR, NOT, NAND,
            NOR, XOR</strong> and <strong>XNOR</strong>. To pass, you read the gate and step onto exactly
            the input combinations whose output is <strong>1</strong> — the same skill as completing a
            truth table, under a little pressure.
          </p>
          <p style={{ fontSize: '0.82rem', color: B.muted, lineHeight: 1.6, margin: 0 }}>
            NAND, NOR and XNOR are simply the inversions of AND, OR and XOR, so their output columns are
            flipped. XOR outputs 1 only when the inputs differ; XNOR only when they match. Clear all seven
            chambers to escape.
          </p>
        </div>
      </main>
    </div>
  );
}
